import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import {
  type ApiError,
  type ApiResponse,
  addActionNameToMutationResult,
  useLegacyAxiosInstance,
} from 'shared/hooks/utils';
import { z } from 'zod/v4';
import { StepsSwitchSchema } from './steps-switch';
import { RECOVERY_PLANS_QUERY_KEY } from './use-load-recovery-plans';

const URL = '/recovery_plan_switch';

const SwitchRecoveryPlanInvoicesVariablesSchema = z
  .object({
    fromPlanId: z.string(),
    toPlanId: z.string(),
    stepsSwitch: StepsSwitchSchema,
  })
  .transform(({ fromPlanId, toPlanId, stepsSwitch }) => ({
    plans_switch: [{ from: fromPlanId, to: toPlanId, steps_switch: stepsSwitch }],
  }));

type SwitchRecoveryPlanInvoicesVariables = z.input<
  typeof SwitchRecoveryPlanInvoicesVariablesSchema
>;

export async function switchRecoveryPlanInvoicesFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: SwitchRecoveryPlanInvoicesVariables,
) {
  const instance = await axiosInstance;

  return instance.post(URL, SwitchRecoveryPlanInvoicesVariablesSchema.parse(variables));
}

export function useSwitchRecoveryPlanInvoices() {
  const client = useQueryClient();
  const axiosInstance = useLegacyAxiosInstance();

  const mutation = useMutation<ApiResponse, ApiError, SwitchRecoveryPlanInvoicesVariables>({
    mutationFn: variables => switchRecoveryPlanInvoicesFn(axiosInstance, variables),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: RECOVERY_PLANS_QUERY_KEY });
    },
  });

  return addActionNameToMutationResult<
    ApiResponse,
    ApiError,
    SwitchRecoveryPlanInvoicesVariables,
    'switchRecoveryPlanInvoices'
  >('switchRecoveryPlanInvoices', mutation);
}
